import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SympheniaLogo } from '@/components/SympheniaLogo'
import { ZweiFaktorEinrichtung } from '@/components/zwei-faktor/ZweiFaktorEinrichtung'
import { authApi } from '@/lib/auth.api'
import { useAuthStore } from '@/lib/auth.store'
import { einrichtungErledigt, einrichtungsTicket } from '@/lib/zwei-faktor'
import type { LoginResponse } from '@/lib/auth.types'

/**
 * AP-38-E — **Einrichten, bevor es weitergeht** (Entscheidung 8.6).
 *
 * Hierher führt `verlangtEinrichtung`, wenn der Server beim Erneuern des Tokens
 * oder beim Firmenwechsel die Einrichtung verlangt. Das Ticket liegt im
 * `sessionStorage` dieses Tabs; danach geht es dorthin zurück, wo die Person war.
 */
export default function ZweiFaktorEinrichtenPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { schliesseAnmeldungAb } = useAuthStore()
  const [ticket] = useState(() => einrichtungsTicket())
  const [ergebnis, setErgebnis] = useState<LoginResponse | null>(null)

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mb-3 flex justify-center">
            <SympheniaLogo size={24} context="content" />
          </div>
          <h1 className="text-base font-semibold text-foreground">{t('zweiFaktor.einrichtenTitel')}</h1>
        </div>
        {!ticket ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">{t('zweiFaktor.ticketFehlt')}</p>
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t('zweiFaktor.zurueck')}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('zweiFaktor.einrichtenPflicht')}</p>
            <ZweiFaktorEinrichtung
              mitGeraet
              starte={async (verfahren) => (await authApi.startSetup(ticket, verfahren)).data}
              bestaetige={async (code, merken) => {
                const { data } = await authApi.confirmSetup(ticket, code, merken)
                setErgebnis(data)
                return data.recoveryCodes ?? []
              }}
              onFertig={async () => {
                if (!ergebnis) return
                await schliesseAnmeldungAb(ergebnis)
                navigate(einrichtungErledigt(), { replace: true })
              }}
              onAbbrechen={() => navigate('/login', { replace: true })}
            />
          </>
        )}
      </div>
    </div>
  )
}
